import { useState } from "react";
import {
    ArrowUpRight,
    CalendarBlank,
    UsersThree,
    Prohibit,
    CheckCircle,
    Tag,
} from "@phosphor-icons/react";
import {
    PROMO_STATUS,
    audienceLabel,
    formatINRWhole,
    formatPrettyDate,
    offerLabel,
} from "./promoUtils";

// A single promotion card. Its actions and highlighted stats adapt to the
// promotion's status (Running Now / AI Suggested / Scheduled / Past).
export default function PromotionCard({ promo, onView, onApprove, onDiscard, onStop }) {
    const [actionLoading, setActionLoading] = useState(false);
    const status = promo?.status;

    const run = async (fn) => {
        if (!fn) return;
        try {
            setActionLoading(true);
            await fn(promo);
        } finally {
            setActionLoading(false);
        }
    };

    // Visual accents per status.
    const accent =
        status === PROMO_STATUS.ACTIVE
            ? { ring: "border-[#bbf7d0]", bar: "bg-[#16a34a]", pillBg: "bg-[#F0FDF4]", pillText: "text-[#16a34a]", dot: "bg-[#16a34a]", label: "Live" }
            : status === PROMO_STATUS.SUGGESTED
            ? { ring: "border-[#e9d5ff]", bar: "bg-[#7c3aed]", pillBg: "bg-[#f5f3ff]", pillText: "text-[#7c3aed]", dot: "bg-[#7c3aed]", label: "AI Suggested" }
            : status === PROMO_STATUS.SCHEDULED
            ? { ring: "border-[#c7d6f5]", bar: "bg-[#3b5fd4]", pillBg: "bg-[#EEF4FF]", pillText: "text-[#3b5fd4]", dot: "bg-[#3b5fd4]", label: "Scheduled" }
            : { ring: "border-gray-200", bar: "bg-gray-400", pillBg: "bg-gray-100", pillText: "text-gray-500", dot: "bg-gray-400", label: "Ended" };

    const isSuggested = status === PROMO_STATUS.SUGGESTED;
    const showEarned = status === PROMO_STATUS.ACTIVE || status === PROMO_STATUS.ENDED;

    return (
        <div
            className={`bg-white rounded-xl border-[1.5px] ${accent.ring} shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden flex flex-col cursor-pointer`}
            onClick={() => onView && onView(promo)}
        >
            {/* Accent header with status + title */}
            <div className="relative">
                <div className={`h-1 w-full ${accent.bar}`} />
                <div className="px-4 pt-3.5 pb-3 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <span
                            className={`flex items-center gap-1.5 ${accent.pillBg} ${accent.pillText} text-[11px] font-semibold px-2.5 py-1 rounded-full`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} />
                            {accent.label}
                        </span>
                        {offerLabel(promo) && (
                            <span className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-[#FF5A5F] bg-[#FFF0F0] px-2 py-1 rounded-full">
                                <Tag size={12} weight="fill" />
                                {offerLabel(promo)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[16px] font-bold text-gray-900 leading-snug line-clamp-2">
                            {promo?.title || "Untitled"}
                        </h3>
                        <ArrowUpRight weight="bold" className="text-[#0B22B2] shrink-0 mt-0.5" size={16} />
                    </div>
                    {promo?.text && (
                        <p className="text-[12.5px] text-gray-500 leading-snug line-clamp-2 mt-1.5">
                            {promo.text}
                        </p>
                    )}
                </div>
            </div>

            <div className="p-4 flex flex-col gap-3 flex-1">

                {/* Dates + audience (non-suggested) */}
                {!isSuggested && (
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                            <CalendarBlank size={14} />
                            <span>
                                {formatPrettyDate(promo?.start_date) || "—"} → {formatPrettyDate(promo?.end_date) || "—"}
                            </span>
                        </div>
                        <div className="inline-flex items-center gap-1.5 self-start bg-[#F4F6FB] border border-gray-200 rounded-full px-2.5 py-1 text-[11px] font-medium text-gray-500">
                            <UsersThree size={13} />
                            {audienceLabel(promo?.target_audience)}
                        </div>
                    </div>
                )}

                {/* Earned stats (active / past only) */}
                {showEarned && (
                    <div className="mt-auto bg-[#FFFBEB] rounded-lg px-3 py-2 flex items-center justify-between">
                        <div>
                            <p className="text-[9.5px] font-bold text-[#92400e] tracking-wide">UPSELL GENERATED</p>
                            <p className="text-[17px] font-bold text-[#d97706] leading-tight">
                                {formatINRWhole(promo?.upsell_generated)}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] text-[#92400e]">bookings</p>
                            <p className="text-[14px] font-bold text-[#d97706]">{promo?.conversions_count || 0}</p>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-auto" onClick={(e) => e.stopPropagation()}>
                    {isSuggested && (
                        <>
                            <button
                                disabled={actionLoading}
                                onClick={() => run(onDiscard)}
                                className="flex-1 py-2 border-[1.5px] border-gray-200 rounded-lg text-[12.5px] font-semibold text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Discard
                            </button>
                            <button
                                disabled={actionLoading}
                                onClick={() => run(onApprove)}
                                className="flex-[2] py-2 rounded-lg bg-[#7c3aed] text-white text-[12.5px] font-bold hover:bg-[#6d28d9] disabled:opacity-60 flex items-center justify-center gap-1.5"
                            >
                                <CheckCircle size={15} weight="bold" />
                                {actionLoading ? "Approving..." : "Approve"}
                            </button>
                        </>
                    )}

                    {(status === PROMO_STATUS.ACTIVE || status === PROMO_STATUS.SCHEDULED) && (
                        <button
                            disabled={actionLoading}
                            onClick={() => run(onStop)}
                            className="flex-1 py-2 border-[1.5px] border-red-200 rounded-lg text-[12.5px] font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                            <Prohibit size={15} weight="bold" />
                            {actionLoading ? "Stopping..." : "Stop Promotion"}
                        </button>
                    )}

                    {status === PROMO_STATUS.ENDED && (
                        <button
                            onClick={() => onView && onView(promo)}
                            className="flex-1 py-2 border-[1.5px] border-gray-200 rounded-lg text-[12.5px] font-semibold text-gray-500 hover:bg-gray-50"
                        >
                            View Details
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
